import React, { useState, useCallback } from 'react';
import { Upload, Palette, Loader2, Image as ImageIcon, AlertCircle } from 'lucide-react';

// --- Constants ---

// API Key - leave as an empty string, Canvas will handle it.
const API_KEY = process.env.REACT_APP_GEMINI_API_KEY;
const API_URL = `https://generativelanguage.googleapis.com/v1beta/models/gemini-2.5-flash-preview-09-2025:generateContent?key=${API_KEY}`;

// --- Color Item Schema ---
// Reusable schema for a single color object
const COLOR_ITEM_SCHEMA = {
  type: "OBJECT",
  properties: {
    name: {
      type: "STRING",
      description: "The common name of the color (e.g., 'Emerald Green', 'Mustard Yellow')."
    },
    hex: {
      type: "STRING",
      description: "The hex code for the color (e.g., '#50C878')."
    },
    suitability: {
      type: "NUMBER",
      description: "A percentage (0-100) representing how suitable this color is for the analyzed skin tone."
    },
    reasoning: {
      type: "STRING",
      description: "A brief, one-sentence explanation for the suitability score."
    }
  },
  required: ["name", "hex", "suitability", "reasoning"]
};

// --- API Schemas ---

// This is the JSON schema we expect the AI to return for the MAIN ANALYSIS.
const RESPONSE_SCHEMA = {
  type: "OBJECT",
  properties: {
    analysis: {
      type: "STRING",
      description: "A brief, one-sentence analysis of the skin tone and undertone (e.g., 'Warm undertone, medium-light skin')."
    },
    mostSuitable: {
      type: "ARRAY",
      description: "An array of 3 'Most Suitable' colors (suitability 85-100%).",
      items: COLOR_ITEM_SCHEMA
    },
    leastSuitable: {
      type: "ARRAY",
      description: "An array of 3 'Least Suitable' colors (suitability 0-59%).",
      items: COLOR_ITEM_SCHEMA
    }
  },
  required: ["analysis", "mostSuitable", "leastSuitable"]
};

// Schema for the single color check
const SINGLE_COLOR_SCHEMA = {
  type: "OBJECT",
  properties: {
    name: {
      type: "STRING",
      description: "The common name of the color that was analyzed (e.g., 'Sky Blue')."
    },
    hex: {
      type: "STRING",
      description: "The hex code for the color (e.g., '#87CEEB')."
    },
    suitability: {
      type: "NUMBER",
      description: "A percentage (0-100) representing how suitable this color is for the analyzed skin tone."
    },
    reasoning: {
      type: "STRING",
      description: "A brief, one-sentence explanation for the suitability score."
    }
  },
  required: ["name", "hex", "suitability", "reasoning"]
};


// --- Helper Functions ---

/**
 * A simple utility to convert a File object to a base64 string.
 * @param {File} file The image file.
 * @returns {Promise<string>} A promise that resolves with the base64 data (without the prefix).
 */
const toBase64 = (file) => new Promise((resolve, reject) => {
  const reader = new FileReader();
  reader.readAsDataURL(file);
  reader.onload = () => {
    // Result looks like "data:image/jpeg;base64,..."
    // We only want the part after the comma.
    const base64Data = reader.result.split(',')[1];
    resolve(base64Data);
  };
  reader.onerror = (error) => reject(error);
});

/**
 * Custom hook for file handling with drag-and-drop support.
 */
function useFileHandler() {
  const [imageFile, setImageFile] = useState(null);
  const [previewUrl, setPreviewUrl] = useState(null);
  const [base64Image, setBase64Image] = useState(null);
  const [isDragging, setIsDragging] = useState(false);

  const processFile = useCallback(async (file) => {
    if (!file || !file.type.startsWith('image/')) {
      // Replaced alert with console.error and a more robust check
      console.error("Invalid file type. Please upload a valid image file (JPEG, PNG, WEBP).");
      // Use a non-blocking way to inform user if possible, or just return
      return;
    }

    setImageFile(file);

    // Create a preview URL
    if (previewUrl) {
      URL.revokeObjectURL(previewUrl);
    }
    setPreviewUrl(URL.createObjectURL(file));

    // Convert to base64 for the API
    try {
      const b64 = await toBase64(file);
      setBase64Image(b64);
    } catch (error) {
      console.error("Error converting file to base64:", error);
      // Replaced alert
    }
  }, [previewUrl]);

  const handleFileChange = useCallback((e) => {
    const file = e.target.files?.[0];
    if (file) {
      processFile(file);
    }
  }, [processFile]);

  const handleDragOver = useCallback((e) => {
    e.preventDefault();
    e.stopPropagation();
    setIsDragging(true);
  }, []);

  const handleDragLeave = useCallback((e) => {
    e.preventDefault();
    e.stopPropagation();
    setIsDragging(false);
  }, []);

  const handleDrop = useCallback((e) => {
    e.preventDefault();
    e.stopPropagation();
    setIsDragging(false);

    const file = e.dataTransfer.files?.[0];
    if (file) {
      processFile(file);
      // Also update the file input element to reflect the dropped file
      const fileInput = document.getElementById('file-upload');
      if (fileInput) {
        fileInput.files = e.dataTransfer.files;
      }
    }
  }, [processFile]);

  const reset = useCallback(() => {
    setImageFile(null);
    setBase64Image(null);
    if (previewUrl) {
      URL.revokeObjectURL(previewUrl);
    }
    setPreviewUrl(null);
  }, [previewUrl]);

  return {
    imageFile,
    previewUrl,
    base64Image,
    isDragging,
    handleFileChange,
    handleDragOver,
    handleDragLeave,
    handleDrop,
    reset
  };
}

// --- Main App Component ---

export default function App() {
  const [results, setResults] = useState(null);
  const [isLoading, setIsLoading] = useState(false);
  const [error, setError] = useState(null);

  // New states for single color search
  const [colorSearchQuery, setColorSearchQuery] = useState("");
  const [colorSearchResult, setColorSearchResult] = useState(null);
  const [isCheckingColor, setIsCheckingColor] = useState(false);
  const [colorSearchError, setColorSearchError] = useState(null);

  const {
    imageFile,
    previewUrl,
    base64Image,
    isDragging,
    handleFileChange,
    handleDragOver,
    handleDragLeave,
    handleDrop,
    reset: resetFileHandler
  } = useFileHandler();

  /**
   * Calls the Gemini API to analyze the image.
   */
  const analyzeImage = async () => {
    if (!base64Image || !imageFile) {
      setError("No image data to analyze.");
      return;
    }

    setIsLoading(true);
    setError(null);
    setResults(null);
    // Reset single color search as well
    setColorSearchResult(null);
    setColorSearchError(null);
    setColorSearchQuery("");

    // Define the prompts for the AI
    const systemPrompt = "You are a world-renowned color scientist and fashion theorist. Your task is to provide an expert analysis of a skin tone from an image. You MUST follow this precise, multi-step logic: \n1. First, analyze the skin tone to determine its two primary properties: \n   - Undertone: (Warm, Cool, or Neutral). \n   - Value/Category: (Fair, Light, Medium/Brown, or Deep/Black). \n2. Second, apply the following expert fashion principles to generate recommendations: \n   - Principle 1 (Undertone): Warm undertones thrive in warm colors (golds, creams, olive, reds). Cool undertones thrive in cool colors (blues, silvers, jewel tones). Neutrals are flexible. \n   - Principle 2 (Contrast & Value): \n     - For 'Fair/Light' skin: High-contrast (like black, navy) is dramatic and striking (High Suitability, 90%+). Low-contrast (like soft pastels) is ethereal (High Suitability, 85%+). Colors too close to the skin tone (like beige, light yellow) will wash the person out (Low Suitability, < 40%). \n     - For 'Medium/Brown' skin: This is the most versatile. It excels with vibrant, saturated colors (like jewel tones, brights) (High Suitability, 90%+). Avoid muted, ashy colors that are too close to the skin's value, as they can look 'muddy' (Low Suitability, < 50%). \n     - For 'Deep/Black' skin: Thrives on rich, vibrant, saturated colors (jewel tones, bright white, bold neons, rich pastels) (High Suitability, 90%+). High contrast is extremely effective. Avoid muted, ashy, or mid-tone colors (like ashy browns/greys) that are too close to the skin's value, as they can look 'dull' (Low Suitability, < 40%). \n3. Third, generate one 'analysis' sentence describing both Undertone and Value. \n4. Fourth, generate a 'mostSuitable' list of 3 colors (85-100%) and a 'leastSuitable' list of 3 colors (0-59%), all based on your deep analysis. \n5. Finally, provide a precise percentage and a one-sentence reasoning for EACH of the 6 colors, strictly following your principles. \n6. Respond ONLY with the requested JSON object.";
    
    const userPrompt = "Analyze the primary skin tone in this image. 1. Provide a brief analysis of the skin's undertone and value. 2. Provide a 'mostSuitable' list of 3 colors. 3. Provide a 'leastSuitable' list of 3 colors. 4. For all 6 colors, provide the name, hex code, a precise suitability percentage (based on your rules), and a one-sentence reasoning.";

    // Construct the API payload
    const payload = {
      contents: [
        {
          role: "user",
          parts: [
            { text: userPrompt },
            {
              inlineData: {
                mimeType: imageFile.type,
                data: base64Image
              }
            }
          ]
        }
      ],
      systemInstruction: {
        parts: [{ text: systemPrompt }]
      },
      generationConfig: {
        responseMimeType: "application/json",
        responseSchema: RESPONSE_SCHEMA,
        temperature: 0 // FULL CONSISTENCY
      }
    };

    // --- API Call with Exponential Backoff ---
    let response;
    let retries = 3;
    let delay = 1000; // 1 second

    for (let i = 0; i < retries; i++) {
      try {
        response = await fetch(API_URL, {
          method: 'POST',
          headers: {
            'Content-Type': 'application/json',
          },
          body: JSON.stringify(payload)
        });

        if (response.ok) {
          break; // Success
        }

        if (response.status === 429 || response.status >= 500) {
          // Throttling or server error, wait and retry
          if (i === retries - 1) {
             throw new Error(`API error after ${retries} attempts: ${response.statusText}`);
          }
          await new Promise(res => setTimeout(res, delay));
          delay *= 2; // Exponential backoff
        } else {
          // Client error (e.g., 400 Bad Request)
          throw new Error(`API client error: ${response.statusText}`);
        }

      } catch (err) {
        if (i === retries - 1) {
          console.error("API call failed after retries:", err);
          setError(`Analysis failed. Please check your network or try again later. Error: ${err.message}`);
          setIsLoading(false);
          return;
        }
        await new Promise(res => setTimeout(res, delay));
        delay *= 2;
      }
    }
    
    // --- Process the successful response ---
    try {
      const result = await response.json();
      const jsonText = result.candidates?.[0]?.content?.parts?.[0]?.text;

      if (!jsonText) {
        throw new Error("No valid content received from AI.");
      }

      const parsedJson = JSON.parse(jsonText);
      
      // Add a safety check here before setting state
      // This MUST be updated to check for the new array names
      if (parsedJson && Array.isArray(parsedJson.mostSuitable) && Array.isArray(parsedJson.leastSuitable)) {
         setResults(parsedJson);
      } else {
         // This can happen if the AI fails to follow the schema
         console.error("Invalid response structure from AI:", parsedJson);
         throw new Error("AI returned an unexpected data format. Missing 'mostSuitable' or 'leastSuitable' arrays.");
      }


    } catch (err) {
      console.error("Error parsing AI response:", err);
      setError(`Failed to process the AI's response. ${err.message}`);
    } finally {
      setIsLoading(false);
    }
  };

  /**
   * Calls the Gemini API to check a single color against the analyzed skin tone.
   */
  const handleColorSearch = async () => {
    if (!colorSearchQuery || !results?.analysis) {
      setColorSearchError("Please type a color name to check. You must run the main analysis first.");
      return;
    }

    // --- NEW LOGIC: Check existing palette first ---
    const normalizedQuery = colorSearchQuery.toLowerCase().trim();
    if (results.mostSuitable && results.leastSuitable) {
      
      const existingMost = results.mostSuitable.find(
        (color) => color.name.toLowerCase().trim() === normalizedQuery
      );
      
      const existingLeast = results.leastSuitable.find(
        (color) => color.name.toLowerCase().trim() === normalizedQuery
      );

      const existingColor = existingMost || existingLeast;

      if (existingColor) {
        // Found it! Bypass the API call.
        setColorSearchResult(existingColor);
        setColorSearchError(null);
        return; // Stop here
      }
    }
    // --- End of new logic ---
    
    // If not found in the palette, proceed with the API call.
    setIsCheckingColor(true);
    setColorSearchError(null);
    setColorSearchResult(null);

    const skinToneAnalysis = results.analysis;
    const colorName = colorSearchQuery;

    // Define the prompts for the AI
    const systemPrompt = "You are a world-renowned color scientist and fashion theorist. Your task is to provide an expert analysis of a single color against a pre-analyzed skin tone. You MUST follow this precise, multi-step logic: \n1. First, accept the pre-analyzed skin tone's properties: \n   - Undertone: (e.g., Warm, Cool, or Neutral). \n   - Value/Category: (e.g., Fair, Light, Medium/Brown, or Deep/Black). \n2. Second, apply the following expert fashion principles to analyze the given color: \n   - Principle 1 (Undertone): Does the color (e.g., 'Olive') match the skin's undertone (e.g., 'Warm')? \n   - Principle 2 (Contrast & Value): \n     - For 'Fair/Light' skin: High-contrast (like black) is striking (90%+). Low-contrast (pastels) is ethereal (85%+). Colors that wash out (beige) are Low Suitability (< 40%). \n     - For 'Medium/Brown' skin: Thrives on vibrant colors (90%+). Avoids ashy/muddy colors (< 50%). \n     - For 'Deep/Black' skin: Thrives on rich, saturated colors (90%+). Avoids ashy/dull colors (< 40%). \n3. Third, generate a precise 'suitability' percentage based *only* on these principles. \n4. Finally, provide a one-sentence reasoning for your score. \n5. Respond ONLY with the requested JSON object.";
    
    const userPrompt = `Given a person with a skin tone described as: "${skinToneAnalysis}". 
    
    Please analyze the suitability of the clothing color: "${colorName}". 
    
    Following your strict suitability scale and rules for neutrals, provide its common name, hex code, a precise suitability percentage, and a brief (one-sentence) reasoning for this score.`;

    // Construct the API payload
    const payload = {
      contents: [
        {
          role: "user",
          parts: [{ text: userPrompt }]
        }
      ],
      systemInstruction: {
        parts: [{ text: systemPrompt }]
      },
      generationConfig: {
        responseMimeType: "application/json",
        responseSchema: SINGLE_COLOR_SCHEMA,
        temperature: 0 // Consistent, deterministic results
      }
    };

    // --- API Call with Exponential Backoff ---
    let response;
    let retries = 3;
    let delay = 1000;

    for (let i = 0; i < retries; i++) {
      try {
        response = await fetch(API_URL, {
          method: 'POST',
          headers: {
            'Content-Type': 'application/json',
          },
          body: JSON.stringify(payload)
        });

        if (response.ok) break; // Success
        if (response.status === 429 || response.status >= 500) {
          if (i === retries - 1) throw new Error(`API error after ${retries} attempts: ${response.statusText}`);
          await new Promise(res => setTimeout(res, delay));
          delay *= 2;
        } else {
          throw new Error(`API client error: ${response.statusText}`);
        }
      } catch (err) {
        if (i === retries - 1) {
          console.error("Single color API call failed:", err);
          setColorSearchError(`Analysis failed. Error: ${err.message}`);
          setIsCheckingColor(false);
          return;
        }
        await new Promise(res => setTimeout(res, delay));
        delay *= 2;
      }
    }
    
    // --- Process the successful response ---
    try {
      const result = await response.json();
      const jsonText = result.candidates?.[0]?.content?.parts?.[0]?.text;
      if (!jsonText) throw new Error("No valid content received from AI.");

      const parsedJson = JSON.parse(jsonText);
      setColorSearchResult(parsedJson);
    } catch (err) {
      console.error("Error parsing single color response:", err);
      setColorSearchError("Failed to process the AI's response.");
    } finally {
      setIsCheckingColor(false);
    }
  };


  const handleReset = () => {
    resetFileHandler();
    setResults(null);
    setError(null);
    setIsLoading(false);

    // Reset single color search state
    setColorSearchQuery("");
    setColorSearchResult(null);
    setIsCheckingColor(false);
    setColorSearchError(null);

    // Reset the file input value
    const fileInput = document.getElementById('file-upload');
    if (fileInput) {
      fileInput.value = "";
    }
  };

  /**
   * Reusable component for rendering a color item
   */
  const ColorItem = ({ color, barClass = "bg-blue-600" }) => (
    <li className="p-4 bg-white border border-gray-200 rounded-lg shadow-sm">
      <div className="flex items-center justify-between mb-2">
        <div className="flex items-center">
          <div 
            className="w-8 h-8 rounded-full border border-gray-300 shadow-inner mr-3"
            style={{ backgroundColor: color.hex }}
            title={`Hex: ${color.hex}`}
          ></div>
          <span className="text-lg font-medium text-gray-800">{color.name}</span>
        </div>
        <span className={`text-base font-semibold ${barClass.includes('blue') ? 'text-blue-600' : 'text-red-600'}`}>
          {color.suitability}%
        </span>
      </div>
      {/* Suitability Bar */}
      <div className="w-full bg-gray-200 rounded-full h-2.5 overflow-hidden">
        <div 
          className={`${barClass} h-2.5 rounded-full transition-all duration-500 ease-out`} 
          style={{ width: `${color.suitability}%` }}
        ></div>
      </div>
      <p className="text-xs text-gray-500 mt-1.5">{color.reasoning}</p>
    </li>
  );

  return (
    <div className="flex justify-center items-start min-h-screen bg-gray-100 font-sans p-4 sm:p-8">
      <div className="w-full max-w-5xl bg-white shadow-2xl rounded-2xl overflow-hidden flex flex-col lg:flex-row">
        
        {/* --- Left Panel: Upload & Image --- */}
        <div 
          className={`w-full lg:w-1/2 p-6 sm:p-8 flex flex-col transition-all duration-300 ${isDragging ? 'bg-blue-50' : 'bg-white'}`}
          onDragOver={handleDragOver}
          onDragLeave={handleDragLeave}
          onDrop={handleDrop}
        >
          <h1 className="text-3xl font-bold text-gray-800 mb-2">AI Skin Tone Color Analyzer</h1>
          <p className="text-gray-600 mb-6">Upload a photo to get a precise AI analysis of your skin tone. Discover your 'Most Suitable' and 'Least Suitable' colors in seconds.</p>
          
          <input
            type="file"
            id="file-upload"
            accept="image/png, image/jpeg, image/webp"
            onChange={handleFileChange}
            className="sr-only"
            disabled={isLoading}
          />
          
          <label 
            htmlFor="file-upload" 
            className={`flex flex-col items-center justify-center w-full h-64 border-2 border-dashed rounded-lg cursor-pointer transition-all duration-300 ${isDragging ? 'border-blue-500 bg-blue-100' : 'border-gray-300 bg-gray-50 hover:bg-gray-100'} ${isLoading ? 'cursor-not-allowed' : ''}`}
          >
            {previewUrl ? (
              <img src={previewUrl} alt="Uploaded preview" className="w-full h-full object-contain rounded-lg p-2" />
            ) : (
              <div className="flex flex-col items-center justify-center pt-5 pb-6 text-gray-500">
                <Upload className="w-10 h-10 mb-3" />
                <p className="mb-2 text-sm font-semibold">Click to upload or drag and drop</p>
                <p className="text-xs">PNG, JPG, or WEBP (max 10MB)</p>
              </div>
            )}
          </label>
          
          {previewUrl && (
            <div className="flex flex-col sm:flex-row gap-4 mt-6">
              <button
                onClick={analyzeImage}
                disabled={isLoading}
                className="flex-1 inline-flex items-center justify-center px-6 py-3 border border-transparent text-base font-medium rounded-lg shadow-sm text-white bg-blue-600 hover:bg-blue-700 focus:outline-none focus:ring-2 focus:ring-offset-2 focus:ring-blue-500 disabled:bg-gray-400 disabled:cursor-not-allowed transition-all"
              >
                {isLoading ? (
                  <>
                    <Loader2 className="mr-2 h-5 w-5 animate-spin" />
                    Analyzing...
                  </>
                ) : (
                  <>
                    <Palette className="mr-2 h-5 w-5" />
                    Analyze Colors
                  </>
                )}
              </button>
              <button
                onClick={handleReset}
                disabled={isLoading}
                className="flex-1 sm:flex-initial inline-flex items-center justify-center px-6 py-3 border border-gray-300 text-base font-medium rounded-lg shadow-sm text-gray-700 bg-white hover:bg-gray-50 focus:outline-none focus:ring-2 focus:ring-offset-2 focus:ring-indigo-500 disabled:opacity-50"
              >
                Reset
              </button>
            </div>
          )}
        </div>
        
        {/* --- Right Panel: Results --- */}
        <div className="w-full lg:w-1/2 p-6 sm:p-8 bg-gray-50 flex flex-col">
          <h2 className="text-2xl font-semibold text-gray-700 mb-4">Your Results</h2>
          <div className="flex-1 overflow-y-auto">
            {/* Loading State */}
            {isLoading && (
              <div className="flex flex-col items-center justify-center h-full text-gray-600">
                <Loader2 className="h-12 w-12 animate-spin text-blue-600 mb-4" />
                <p className="text-lg font-medium">AI is analyzing your photo...</p>
                <p className="text-sm">This may take a moment.</p>
              </div>
            )}
            
            {/* Error State */}
            {error && (
              <div className="flex flex-col items-center justify-center h-full text-red-600 bg-red-50 p-6 rounded-lg">
                <AlertCircle className="h-12 w-12 mb-4" />
                <p className="text-lg font-medium text-center">Analysis Failed</p>
                <p className="text-sm text-center">{error}</p>
              </div>
            )}
            
            {/* Initial State */}
            {!isLoading && !results && !error && (
              <div className="flex flex-col items-center justify-center h-full text-gray-500 text-center">
                <ImageIcon className="h-16 w-16 mb-4" />
                <p className="text-lg font-medium">Your recommended colors will appear here.</p>
                <p className="text-sm">Upload an image and click "Analyze" to begin.</p>
              </div>
            )}
            
            {/* Results State */}
            {results && (
              <div className="animate-fade-in space-y-6">
                <div>
                  <h3 className="text-sm font-semibold text-gray-500 uppercase tracking-wider mb-2">AI Analysis</h3>
                  <p className="text-lg text-gray-800 bg-blue-50 p-4 rounded-lg border border-blue-200">
                    {results.analysis}
                  </p>
                </div>
                
                {/* --- Most Suitable List --- */}
                {results.mostSuitable && Array.isArray(results.mostSuitable) && (
                  <div>
                    <h3 className="text-sm font-semibold text-green-700 uppercase tracking-wider mb-3">Most Suitable</h3>
                    <ul className="space-y-4">
                      {results.mostSuitable.map((color) => (
                        <ColorItem key={color.hex || color.name} color={color} barClass="bg-green-600" />
                      ))}
                    </ul>
                  </div>
                )}
                
                {/* --- Least Suitable List --- */}
                {results.leastSuitable && Array.isArray(results.leastSuitable) && (
                  <div>
                    <h3 className="text-sm font-semibold text-red-700 uppercase tracking-wider mb-3">Least Suitable</h3>
                    <ul className="space-y-4">
                      {results.leastSuitable.map((color) => (
                        <ColorItem key={color.hex || color.name} color={color} barClass="bg-red-600" />
                      ))}
                    </ul>
                  </div>
                )}


                {/* --- New Color Search Feature --- */}
                <div className="pt-6 border-t border-gray-200">
                  <h3 className="text-sm font-semibold text-gray-500 uppercase tracking-wider mb-3">Check a Specific Color</h3>
                  <div className="flex flex-col sm:flex-row gap-2">
                    <input
                      type="text"
                      value={colorSearchQuery}
                      onChange={(e) => setColorSearchQuery(e.target.value)}
                      placeholder="e.g., 'Mustard Yellow' or '#FFDB58'"
                      className="flex-1 block w-full px-3 py-2 border border-gray-300 rounded-lg shadow-sm focus:outline-none focus:ring-2 focus:ring-blue-500 focus:border-blue-500"
                      disabled={isCheckingColor}
                    />
                    <button
                      onClick={handleColorSearch}
                      disabled={isCheckingColor || !colorSearchQuery}
                      className="inline-flex items-center justify-center px-5 py-2 border border-transparent text-base font-medium rounded-lg shadow-sm text-white bg-blue-600 hover:bg-blue-700 focus:outline-none focus:ring-2 focus:ring-offset-2 focus:ring-blue-500 disabled:bg-gray-400 disabled:cursor-not-allowed transition-all"
                    >
                      {isCheckingColor ? (
                        <Loader2 className="h-5 w-5 animate-spin" />
                      ) : (
                        "Check"
                      )}
                    </button>
                  </div>

                  {/* Single Color Search Error */}
                  {colorSearchError && (
                    <p className="text-sm text-red-600 mt-2">{colorSearchError}</p>
                  )}

                  {/* Single Color Search Result */}
                  {colorSearchResult && (
                    <div className="mt-4 p-4 bg-white border border-gray-200 rounded-lg shadow-sm animate-fade-in">
                      <div className="flex items-center justify-between mb-2">
                        <div className="flex items-center">
                          <div 
                            className="w-8 h-8 rounded-full border border-gray-300 shadow-inner mr-3"
                            style={{ backgroundColor: colorSearchResult.hex }}
                            title={`Hex: ${colorSearchResult.hex}`}
                          ></div>
                          <span className="text-lg font-medium text-gray-800">{colorSearchResult.name}</span>
                        </div>
                        <span 
                          className={`text-base font-semibold ${
                            colorSearchResult.suitability >= 85 ? 'text-green-600' :
                            colorSearchResult.suitability >= 60 ? 'text-yellow-600' :
                            'text-red-600'
                          }`}
                        >
                          {colorSearchResult.suitability}%
                        </span>
                      </div>
                      <div className="w-full bg-gray-200 rounded-full h-2.5 overflow-hidden mb-1.5">
                        <div 
                          className={`h-2.5 rounded-full transition-all duration-500 ease-out ${
                            colorSearchResult.suitability >= 85 ? 'bg-green-600' :
                            colorSearchResult.suitability >= 60 ? 'bg-yellow-500' :
                            'bg-red-600'
                          }`}
                          style={{ width: `${colorSearchResult.suitability}%` }}
                        ></div>
                      </div>
                      <p className="text-sm text-gray-700">{colorSearchResult.reasoning}</p>
                    </div>
                  )}
                </div>

              </div>
            )}
          </div>
        </div>
      </div>
      
      {/* Simple fade-in animation */}
      <style>{`
        .animate-fade-in {
          animation: fadeIn 0.5s ease-out;
        }
        @keyframes fadeIn {
          from { opacity: 0; transform: translateY(10px); }
          to { opacity: 1; transform: translateY(0); }
        }
        
        /* Ensure the results panel scrolls if content overflows */
        .overflow-y-auto {
          max-height: calc(100vh - 4rem); /* Adjust as needed */
        }
        @media (min-width: 1024px) {
          .lg\\:w-1\\/2 .overflow-y-auto {
             /* 8rem for padding (p-8 * 2) */
            max-height: calc(100vh - 8rem);
          }
        }
      `}</style>
    </div>
  );
}






